import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SEOHead = ({ 
  title, 
  description, 
  image, 
  type = 'website',
  noindex = false 
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lang = i18n.language;
  
  // Base URL for the site
  const baseUrl = 'https://www.offroaditalia.com';
  const currentUrl = `${baseUrl}${location.pathname}`;
  
  // Default SEO content per language
  const defaultSEO = {
    it: {
      title: 'Offroad Italia - Tour Moto Enduro e Rally dal 2011',
      description: 'Organizziamo tour offroad in moto enduro e rally in Europa, Africa e Sudamerica. Oltre 200 recensioni a 5 stelle. Prenota la tua avventura!'
    },
    en: {
      title: 'Offroad Italia - Enduro & Rally Motorcycle Tours Since 2011',
      description: 'We organize offroad motorcycle tours in Europe, Africa, and South America. Over 200 5-star reviews. Book your adventure today!'
    },
    de: {
      title: 'Offroad Italia - Enduro & Rally Motorradtouren seit 2011',
      description: 'Wir organisieren Offroad-Motorradtouren in Europa, Afrika und Südamerika. Über 200 5-Sterne-Bewertungen. Buchen Sie Ihr Abenteuer!'
    },
    fr: {
      title: 'Offroad Italia - Tours Moto Enduro & Rally depuis 2011',
      description: 'Nous organisons des tours moto offroad en Europe, Afrique et Amérique du Sud. Plus de 200 avis 5 étoiles. Réservez votre aventure!'
    },
    es: {
      title: 'Offroad Italia - Tours Moto Enduro y Rally desde 2011',
      description: 'Organizamos tours offroad en moto por Europa, África y Sudamérica. Más de 200 reseñas de 5 estrellas. ¡Reserva tu aventura!'
    }
  };
  
  const seo = defaultSEO[lang] || defaultSEO.it;
  const finalTitle = title || seo.title;
  const finalDescription = description || seo.description;
  const finalImage = image || `${baseUrl}/images/og-image.jpg`;
  
  useEffect(() => {
    // Update document title
    document.title = finalTitle;
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = finalDescription;
    
    // Update language
    document.documentElement.lang = lang;
    
    // Open Graph tags
    const ogTags = {
      'og:title': finalTitle,
      'og:description': finalDescription,
      'og:image': finalImage,
      'og:url': currentUrl,
      'og:type': type,
      'og:locale': lang === 'it' ? 'it_IT' : lang === 'en' ? 'en_US' : lang === 'de' ? 'de_DE' : lang === 'fr' ? 'fr_FR' : 'es_ES',
      'og:site_name': 'Offroad Italia'
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
    
    // Twitter Card tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': finalTitle,
      'twitter:description': finalDescription,
      'twitter:image': finalImage
    };
    
    Object.entries(twitterTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = currentUrl;
    
    // Alternate language links (hreflang)
    const languages = ['it', 'en', 'de', 'fr', 'es'];
    languages.forEach(lng => {
      let hreflang = document.querySelector(`link[rel="alternate"][hreflang="${lng}"]`);
      if (!hreflang) {
        hreflang = document.createElement('link');
        hreflang.rel = 'alternate';
        hreflang.hreflang = lng;
        document.head.appendChild(hreflang);
      }
      hreflang.href = `${baseUrl}${location.pathname}?lang=${lng}`;
    });
    
    // x-default hreflang
    let xDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (!xDefault) {
      xDefault = document.createElement('link');
      xDefault.rel = 'alternate';
      xDefault.hreflang = 'x-default';
      document.head.appendChild(xDefault);
    }
    xDefault.href = currentUrl;
    
    // Robots meta
    if (noindex) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex, nofollow';
    }
    
  }, [finalTitle, finalDescription, finalImage, currentUrl, lang, type, noindex, location.pathname]);
  
  return null; // This component doesn't render anything
};

export default SEOHead;
